import {
    Subscription,
    Subject,
    Observable,
    startWith,
    map
} from "rxjs";

import {
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    booleanAttribute,
    HostListener,
    inject
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    FormControl, ControlValueAccessor, NgControl,
    Validator,
    Validators, FormsModule, ReactiveFormsModule,
    ControlContainer,
    NgForm,
    ValidationErrors
} from "@angular/forms";
import {
    MatAutocomplete,
    MatAutocompleteSelectedEvent,
    MatAutocompleteModule
} from "@angular/material/autocomplete";
import {
    MatChipInputEvent, MatChipsModule
} from "@angular/material/chips";
import { MatFormFieldControl } from "@angular/material/form-field";
import { FormUtil } from "../../forms/form-util";
import {
    COMMA, ENTER
} from "@angular/cdk/keycodes";
import { MatIconModule } from "@angular/material/icon";
import { LiveAnnouncer } from "@angular/cdk/a11y";
import { AsyncPipe } from "@angular/common";
import { MatInput } from "@angular/material/input";

import { CommonValidators } from "../../custom-validators/common-validators";
import {
    StringUtil,
    ArrayUtil,
    CommonRegex
} from "@shopliftr/common-js/data";

import {
    LocalStorage
} from "../../enum/local-storage";
import { EnhancedControlDirective } from "../../forms/directives/form-control/enhanced-control.directive";

/**
 * Email  Form Control Component
 * Allows user to enter an email address. Each email address entry will added to the
 * user's local storage and will be available in the autocomplete menu.
 *
 * Allows for single email address entry when multiple is false
 *
 * Allows for entering multiple email addresses when multiple is true.
 * Email addresses will be show in a chip grid list at the top of the control.
 *
 * This follows the custom form control pattern
 */
@Component({
    selector: "mgd-email",
    templateUrl: "./email.component.html",
    imports: [
        MatAutocompleteModule,
        MatChipsModule,
        MatIconModule,
        MatAutocompleteModule,
        FormsModule,
        ReactiveFormsModule,
        AsyncPipe,
        MatInput
    ],
    providers: [{
        provide: MatFormFieldControl,
        useExisting: EmailComponent
    },
    {
        provide: ControlContainer,
        useExisting: NgForm
    }],
    hostDirectives: [EnhancedControlDirective]
})
export class EmailComponent implements ControlValueAccessor, MatFormFieldControl<Array<string> | string>,
OnChanges, OnDestroy, OnInit, Validator {

    /**
     * nextId used in generating id for this component
     *
     * @static
     * @memberof EmailComponent
     */
    static nextId = 0;

    /**
     * MatFormFieldControl property id
     * Returns the ID of an element in the component's template for <mat-form-field> to associate all of its labels
     * and hints with. Use nextId to generate a unique string when created.
     * Used in setDescribedByIds to set element id that will be used for the aria-described by attribute of this control.
     * @memberof EmailComponent
     */
    @HostBinding() id = `email-${EmailComponent.nextId++}`;

    @HostBinding("attr.aria-describedby") describedBy = "";

    @ViewChild("emailComponentInput") input: ElementRef<HTMLInputElement>;
    @ViewChild("auto") matAutocomplete: MatAutocomplete;

    /**
     * Output event emitted when an option is selected from the Mat Autocomplete dropdown
     * MatSelect or other elements that selects
     * an item from a list.
     *
     *
     * @memberof EmailComponent
     */
    @Output() optionSelected = new EventEmitter<string>(false);

    /**
     * MatFormFieldControl property that tells the form field when it should be in the disabled state.
     * In addition to reporting the right state to the form field, we need to set the disabled state
     * on the individual inputs that make up our component.
     *
     * @type {boolean}
     * @memberof EmailComponent
     */
    @Input({ transform: booleanAttribute }) disabled: boolean = false;

    /**
     * MatFormFieldControl property that tells <mat-form-field> what to use as a placeholder
     *
     * @readonly
     * @type {string}
     * @memberof EmailComponent
     */
    @Input() placeholder: string;

    /**
     * The input used to set the required value.
     *
     * Using a aliased entry allows allows for a required setter and getter
     *
     * ngOnChanges will call the required setter when this input changes
     */
    @Input("required") requiredInput: boolean;

    /**
     * set to true if the control should allow multiple emails, false if only one email should be selected
     *
     * @type {boolean}
     * @memberof EmailComponent
     */
    @Input("multiple") multipleInput: boolean;

    /**
     * MatFormFieldControl property used to indicate whether the input is required.
     * <mat-form-field> uses this information to add a required indicator to the placeholder.
     *
     * @readonly
     * @type {boolean}
     * @memberof EmailComponent
     */
    get required(): boolean {
        return this._required || this.ngControl?.control?.hasValidator(Validators.required);
    }

    set required(value: boolean) {

        this._required = value;
        this.stateChanges.next();
    }

    /**
     * multiple attribute indicates if the component will accept more than one email.
     * If multiple is true, then the component will accept more than one email and
     * present each email as a chip in a chip list above the input field.
     * If multiple is set to false, then the component will only have the
     *  input field and the user can only input one email address.
     * @type {boolean}
     * @memberof EmailComponent
     */
    get multiple(): boolean {
        return this._multiple;
    }

    /**
     * Multiple attribute when set to true allows the user to enter more than one email.
     * Setting multiple to false will only allow one email.
     *
     * @memberof EmailComponent
     */
    set multiple(value: boolean) {
        this._multiple = value;
        this.stateChanges.next();
    }

    /**
     * The different characters that can be used to mark the end of an email string token.
     *
     * @type {Array<number>}
     * @memberof EmailComponent
     */
    separatorKeysCodes: Array<number> = [ENTER, COMMA];

    /**
     * Internal form component to represent the input field the where the user will enter an email address
     *
     * @memberof EmailComponent
     */
    emailFormControl = new FormControl(undefined, [CommonValidators.emailValidator()]);

    /**
     * Observable that represents the email addresses that are filtered by the users's input
     *
     * @type {Observable<Array<string>>}
     * @memberof EmailComponent
     */
    filteredEmails$: Observable<Array<string>>;

    /**
     * enteredEmails is the array of emails that the user has selected.
     * This should appear in chip grid list and will be the value returned by the component if multiple is true.
     *
     * @type {Array<string>}
     * @memberof EmailComponent
     */
    enteredEmails: Array<string> = [];

    /**
     * All the emails that are collected from the user and will be presented in the autocomplete suggestions
     *
     * @type {Array<string>}
     * @memberof EmailComponent
     */
    allEmails: Array<string> = [];

    /**
     * LiveAnnouncer service to announce messages throughout the component
     *
     * @memberof EmailComponent
     */
    readonly announcer = inject(LiveAnnouncer);

    /**
     * The ngControl directive for this component
     */
    readonly ngControl = inject(NgControl, {
        optional: true,
        self: true
    });

    /**
     * Subscription for any state changes that are emitted from the ngControl
     *
     * @type {Subscription}
     * @memberof EmailComponent
     */
    stateChangesSubscription: Subscription;

    /**
     * MatFormFieldControl property that specifies a unique string for the type of control in form field.
     * The <mat-form-field> will add a class based on this type that can be used to easily apply special
     * styles to a <mat-form-field> that contains a specific type of control.
     * The form field will add the class mat-form-field-type-example-form-control.
     *
     * @memberof EmailComponent
     */
    controlType = "email";

    /**
     * MatFormFieldControl property indicates whether the associated NgControl is in an error state.
     * Can show an error if the input is invalid and our component has been touched.
     *
     * @memberof EmailComponent
     */
    errorState = false;

    /**
     * MatFormFieldControl property indicates whether or not the form field control should be considered to be in a focused state.
     *
     * @memberof EmailComponent
     */
    focused = false;

    /**
     * MatFormFieldControl property
     * MatFormField uses OnPush strategy, stateChanges will tell the parent that something happened
     * in the form field control that may require the form field to run change detection.
     *
     * @memberof EmailComponent
     */
    stateChanges = new Subject<void>();

    /**
     * Callback function registered with registerOnChange
     *
     * @memberof EmailComponent
     */
    onChange: (_: any) => void;

    /**
     * Callback function registered with registerOnTouched
     *
     * @memberof EmailComponent
     */
    onTouched: () => void;

    /**
     * Placeholder logic for the input field. It will only show the placeholder if the mat label is floating or when input field is focused.
     *
     * @readonly
     * @type {string}
     * @memberof EmailComponent
     */
    get inputPlaceholder(): string {
        if (this.shouldLabelFloat && this.placeholder) {
            return this.placeholder;
        }
        else {
            return "";
        }
    }

    /**
     *  MatFormFieldControl property indicates whether the form field control is empty.
     *  For our control, we'll consider it empty if all of the parts are empty.
     *
     * @readonly
     * @type {boolean}
     * @memberof EmailComponent
     */
    get empty(): boolean {

        if (this._multiple) {
            return FormUtil.emptyValue(this.value) && (!this.multiple || ArrayUtil.isEmpty(this.enteredEmails));
        }
        else {
            return FormUtil.emptyValue(this.value);
        }
    }

    /**
     * MatFormFieldControl property used to indicate whether the label should be in the floating position.
     * Use same logic as matInput and float the placeholder when the input is focused or non-empty.
     * Since the placeholder will be overlapping our control when when it's not floating,
     * should hide the – characters when it's not floating.
     *
     * @readonly
     * @type {boolean}
     * @memberof EmailComponent
     */
    get shouldLabelFloat(): boolean {

        return this.focused || !this.empty || this._shouldLabelFloat;
    }

    /**
     * Setter for MatFormFieldControl property
     *
     * @memberof EmailComponent
     */
    set shouldLabelFloat(value: boolean) {

        this._shouldLabelFloat = value;
    }

    private _shouldLabelFloat: boolean = false;

    /**
     * MatFormFieldControl property value
     *
     * @type {string}
     * @memberof EmailComponent
     */
    get value(): Array<string> | string {

        if (this._multiple) {
            return this.enteredEmails;
        }
        else {
            return StringUtil.sanitizeString(this.emailFormControl.value as string) ?? "";
        }
    }

    /**
     * Setter for MatFormFieldControl property value
     * Also, calls stateChanges.next,  if onChange is registered will call the callback functions for onChange and onTouched
     *
     * @memberof EmailComponent
     */
    set value(newValue: string | Array<string>) {

        if (!this._multiple) {

            newValue = newValue || undefined;
            this.emailFormControl.setValue(StringUtil.sanitizeString(newValue as string), { emitEvent: false });
        }
        else {

            const newValues = new Set<string>();
            // Sanitize and remove empty or undefined values from the array
            // Using the set will ensure uniqueness
            (newValue as Array<string>)?.forEach((value) => {
                const sanitized = StringUtil.sanitizeString(value);
                if (sanitized) {
                    newValues.add(sanitized);
                }
            });
            this.enteredEmails = Array.from(newValues);

        }

        this.stateChanges.next();

        this.onChange?.(newValue);

        this._setErrorState();
    }

    private _required: boolean;

    private _multiple: boolean;

    private readonly _destroyRef = inject(DestroyRef);

    constructor() {

        if (this.ngControl) {
            // Note: we provide the value accessor through here, instead of
            // the `providers` to avoid running into a circular import.
            this.ngControl.valueAccessor = this;

        }
    }

    /**
     * Listener for focus event. It will set the focused state and trigger state changes
     */
    @HostListener("focus") onFocus(): void {
        if (!this.disabled) {
            this.focused = true;
            this.stateChanges.next();
            this.input.nativeElement.focus();
        }
    }

    /**
     * Listener for blur event. It will set the focused state and trigger state changes
     */
    @HostListener("blur") onBlur(): void {
        this.focused = false;
        if (!this.disabled) {
            // NOTE: This is a user interaction, as blur is called when the user leaves the input field,
            // call OnTouched() function here
            this.onTouched?.();

            const currentEmail: string = StringUtil.sanitizeString(this.emailFormControl.value as string) ?? "";
            if (!this._multiple) {
                if (currentEmail) {
                    this.value = currentEmail;
                }
                else if (!currentEmail) {
                    this.value = undefined;
                }
            }
            else {
                this._addEmail(currentEmail);
            }

            this._updateAndPersistAllEmails();

            this.stateChanges.next();
            this._setErrorState();
        }
    }

    /**
     * Angular onChanges Lifecycle event
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (changes.disabled) {
            this._setDisabledState(changes.disabled.currentValue as boolean);
        }

        if (changes.requiredInput) {
            this.required = changes.requiredInput.currentValue;
        }

        if (changes.placeholder) {
            this.stateChanges.next();
        }

        if (changes.multipleInput) {
            const previousValue = this.value || undefined;

            this.multiple = changes.multipleInput.currentValue;
            if (!previousValue) {
                return;
            }
            if (this.multiple) {
                // Changed to multiple selection, clear the enteredEmails array
                if (previousValue && StringUtil.isString(previousValue) && !FormUtil.emptyValue(previousValue)) {
                    this.enteredEmails = [];
                    this.enteredEmails.push(previousValue as string);
                }
                else if (previousValue && Array.isArray(previousValue)) {
                    this.enteredEmails = previousValue;
                }
                // Clear input field
                this.emailFormControl.setValue(undefined, { emitEvent: false });
                this.value = this.enteredEmails;
            }
            else {

                // value was already set to a string, nothing to change
                if (previousValue && StringUtil.isString(previousValue) && !FormUtil.emptyValue(previousValue)) {
                    return;
                }

                // Set first value in enteredEmails array to the value of the component
                const newValue = this.enteredEmails?.[0];
                this.enteredEmails = [];
                this.value = newValue;
            }
            this.stateChanges.next();
        }

        this._setErrorState();
    }

    /**
     * Angular onDestroy Lifecycle event
     */
    ngOnDestroy(): void {

        this.stateChanges.complete();

        this.stateChangesSubscription.unsubscribe();

    }

    /**
     * Angular onInit Lifecycle event
     *
     * Initialize filteredEmails$ observable for auto complete and initialize emailsList
     */
    ngOnInit(): void {

        // Setup validators for the ngControl. May need to merge with parent form's validators.
        if (this.ngControl?.control) {
            this.ngControl.control.addValidators([this.validate.bind(this)]);
            this.ngControl.control.updateValueAndValidity();
        }

        // Initialize email lists
        this._updateAndPersistAllEmails();

        // Setup filteredEmails$ for autocomplete filtering
        this.filteredEmails$ = this.emailFormControl.valueChanges
            .pipe(
                startWith(""),
                map((email: string | null) => (email ? this._filter(email) : this.allEmails.slice())),
            );

        // Setup subscription for all status changes from ngControl to ensure error state is set correctly
        this.stateChangesSubscription = this.ngControl.statusChanges
            .pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => this._setErrorState());
    }

    /**
     * ControlValueAccessor interface method used to register a callback function that will be called whenever the value
     * of the custom form control changes. The callback function is passed as an argument.
     * @param fn
     */
    registerOnChange(fn: any): void {

        this.onChange = fn;
    }

    /**
     * ControlValueAccessor interface method registers the onTouched() callback function, that will be called
     * when the custom form control is touched.
     * @param fn
     */
    registerOnTouched(fn: any): void {

        this.onTouched = fn;
    }

    /**
     * ControlValueAccessor interface method that updates the value of the custom control
     * @param obj
     */
    writeValue(obj: string | Array<string>): void {

        // Set value of internal custom controls
        if (this.multiple) {
            if (Array.isArray(obj)) {
                this.value = obj;
            }
            else if (obj) {
                this.value = [obj];
            }
            else {
                this.value = [];
            }
        }
        else {
            this.value = obj;
        }

    }

    validate(): ValidationErrors | null {

        return this.emailFormControl.errors;
    }

    /**
     * MatFormFieldControl method will be called when the form field is clicked on.
     */
    onContainerClick(): void {

        if (!this.disabled) {
            this.input.nativeElement.focus();
        }
    }

    /**
     * MatFormFieldControl method used by the <mat-form-field> to set element ids that should be used for the aria-described by attribute.
     * The ids are controlled through the form field as hints or errors are conditionally displayed and should be reflected in the control's
     * aria-described by attribute for an improved accessibility experience.
     * The setDescribedByIds method is invoked whenever the control's state changes.
     * @param ids
     */
    setDescribedByIds(ids: Array<string>): void {

        this.describedBy = ids.join(" ");
    }

    /**
     * ControlValueAccessor Function that is called by the forms API when the control status changes to or from 'DISABLED'.
     * Depending on the status, it enables or disables the appropriate DOM element.
     * @param isDisabled boolean value that indicates whether the control should be disabled or not
     */
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this._setDisabledState(isDisabled);
    }

    /**
     * This function is called when optionSelected is fired from the Material Autocomplete or Material Select control.
     * This happens when the user selects an item from the list.
     * Notifies the parent when an autocomplete option has been explicitly selected
     */
    onOptionSelected(event: MatAutocompleteSelectedEvent): void {

        if (this._multiple) {
            this._addEmail(event.option.viewValue);
        }
        else {
            this.value = event.option.viewValue;
        }

        this._updateAndPersistAllEmails();

        // NOTE: This is a user interaction, as this is called when the user selects from MatOption List,
        // call OnTouched() function here
        this.onTouched?.();

        this._setErrorState();

        this.optionSelected.emit(event.option.value as string);
    }

    /**
     * onInputTokenEnd function called when user enters the Enter key or comma in the input field.
     * calls add function to add the email to the entered email list and add chip to the chip list.
     * @param event MatChipInputEvent that contains the value of the input field
     */
    public onInputTokenEnd(event: MatChipInputEvent): void {
        const value = StringUtil.sanitizeString(event.value as string) ?? "";
        this._addEmail(value, event);
    };

    /**
     * Remove functions called when the user clicks on the remove icon of a chip in the chip grid list.
     * The email is removed from the enteredEmails array and the value is updated.
     * @param email
     */
    public remove(email: string): void {
        const index = this.enteredEmails.indexOf(email);

        if (index >= 0) {
            this.enteredEmails.splice(index, 1);
            this.value = this.enteredEmails;
        }
    }

    /**
     * Add function used to add email to the entered email list.
     * Also adds MatChip to the ChipList
     * @param newEmail
     * @param event
     */
    private _addEmail(newEmail: string, event?: MatChipInputEvent): void {

        // only used if multiple is set to true
        if (this._multiple) {

            // Add email
            const isValid = this._isValidEntry(newEmail);
            const isUnique = !this.enteredEmails.includes(newEmail);
            if (newEmail && isValid && isUnique) {

                this.enteredEmails.push(newEmail);

                // Clear the input value
                event?.chipInput!.clear();
                this._updateAndPersistAllEmails();

                this.emailFormControl.setValue(null);
                this.value = this.enteredEmails;
            }
            else if (!isUnique) {
                this.emailFormControl.setValue(null);

            }
            else if (!isValid) {
                this.ngControl.control.updateValueAndValidity();
            }
        }
    }

    /**
     * function used to filter the emails in the autocomplete dropdown list based on the input value.
     * @param value
     * @returns list of emails that match the input value
     */
    private _filter(value: string): Array<string> {

        const filterValue = value.toLowerCase();

        return this.allEmails.filter((email) =>
            StringUtil.isString(email) && email.toLowerCase().includes(filterValue));
    }

    /**
     * Set this.errorState based on the ngControl state
     */
    private _setErrorState(): void {

        if (this.multiple) {
            // check if there is something in the input field
            const inputValue = StringUtil.sanitizeString(this.emailFormControl.value as string);
            if (inputValue) {
                this.errorState = this.ngControl.touched && this.ngControl.invalid;
            }
            else if (this.required) {
                this.errorState = this.ngControl.touched
                    && (this.enteredEmails.length === 0);
            }
            else {
                this.errorState = false;
            }
        }
        else {
            this.errorState = this.ngControl.touched && this.ngControl.invalid;
        }

    }

    /**
     * Disable or enable elements in the custom control
     */
    private _setDisabledState(isDisabled: boolean): void {

        if (isDisabled) {
            this.emailFormControl.disable();

        }
        else {
            this.emailFormControl.enable();
        }

        this.stateChanges.next();
    }

    private _isValidEntry(entry: string): boolean {

        if (!entry) {
            return false;
        }

        return new RegExp(CommonRegex.EMAIL, "i").test(String(entry).toLowerCase());
    }

    /**
     * Merge all emails from the enteredEmails array and the savedEmails from localStorage.
     */
    private _updateAndPersistAllEmails(): void {

        if (ArrayUtil.isEmpty(this.allEmails)) {

            let savedEmails: Array<string> = JSON.parse(localStorage.getItem(LocalStorage.SavedEmails));

            if (savedEmails) {
                savedEmails = savedEmails.filter((entry) => {

                    return this._isValidEntry(entry);
                });
            }

            this.allEmails = savedEmails ? savedEmails : [];
        }

        this.allEmails = this.allEmails.concat(this.enteredEmails.filter((item) => {

            return (!this.allEmails.includes(item));
        }));

        if (!this._multiple) {
            const currentEmail: string = StringUtil.sanitizeString(this.emailFormControl.value as string);
            if (currentEmail && this._isValidEntry(currentEmail) && !this.allEmails.includes(currentEmail)) {
                this.allEmails.push(currentEmail);
            }
        }

        localStorage.setItem(LocalStorage.SavedEmails, JSON.stringify(this.allEmails));

    }

}
